import React from 'react';
import cx from 'classnames';

import Modal from '~/components/modals/modal';
import { Field, Form } from '~/components/form';
import InputField from '~/components/inputs/input-field';
import Button from '~/components/button';
import { required, valid, phone, minLength } from '~/utils/validation';
import { useFlashMessage } from '~/features/flash-messages/flash-message-actions';
import { useSession } from '~/features/session/session-actions';
import { useUpdateShippingAddress } from '~/features/user/user-actions';
import { toApiPhone } from '~/features/addresses/address';
import StateSelectField from '~/components/inputs/state-select-field';
import {
  getValidationErrorMessageFor,
  isValidationError,
} from '~/api/api-errors';
import { analyticsBeacon, EventType } from '~/utils/analytics';
import { PHONE_MASK } from '~/utils/input-masks';

import styles from '~/components/modals/modal.module.scss';

const CreateShippingAddressModal = ({ onClose, isOpen }) => {
  const { user } = useSession();
  const { showFlashMessage } = useFlashMessage();
  const { createShippingAddress } = useUpdateShippingAddress();

  const handleSubmit = async (formValues) => {
    try {
      // only set the default if the user has no other addresses
      const isDefault = user?.addresses.length === 0;

      const newShippingAddress = {
        userUuid: user.uuid,
        firstName: formValues.firstName,
        lastName: formValues.lastName,
        line1: formValues.line1,
        line2: formValues.line2,
        city: formValues.city,
        stateCode: formValues.stateCode,
        countryCode: 'US',
        postalCode: formValues.postalCode,
        phoneNumber: toApiPhone(formValues.phone),
        isDefault,
      };

      await createShippingAddress(newShippingAddress);

      onClose();

      // TODO: create beacon event for address creation?
      analyticsBeacon.emit(EventType.ACCOUNT_UPDATED, {
        userUuid: user.uuid,
        firstName: user.firstName,
        lastName: user.lastName,
        city: formValues.city,
        state: formValues.stateCode,
        zip: formValues.postalCode,
        country: 'US',
        email: user.email,
        formattedAddress: `${formValues.line1} ${formValues.line2} ${formValues.city}, ${formValues.stateCode} ${formValues.postalCode} ${formValues.countryCode}`,
        phone: formValues.phone,
      });
    } catch (err) {
      if (isValidationError(err)) {
        return {
          firstName: getValidationErrorMessageFor(err, 'firstName'),
          lastName: getValidationErrorMessageFor(err, 'lastName'),
          line1: getValidationErrorMessageFor(err, 'line1'),
          line2: getValidationErrorMessageFor(err, 'line2'),
          city: getValidationErrorMessageFor(err, 'city'),
          stateCode: getValidationErrorMessageFor(err, 'stateCode'),
          postalCode: getValidationErrorMessageFor(err, 'postalCode'),
          phone: getValidationErrorMessageFor(err, 'phoneNumber'),
        };
      } else {
        showFlashMessage(
          'We could not create a shipping address at this time. Please try again later',
          'error'
        );
      }
    }
  };

  return (
    <Modal
      onClose={onClose}
      isOpen={isOpen}
      className={styles.accountInfoModal}
    >
      <h1 className={cx(styles.title, styles.accountInfoModalTitle)}>
        Add Address
      </h1>
      <Form
        initialValues={{
          firstName: '',
          lastName: '',
          line1: '',
          line2: '',
          city: '',
          stateCode: '',
          postalCode: '',
          phoneNumber: '',
        }}
        onSubmit={handleSubmit}
        validate={validate}
        resetOnSubmit
      >
        {({ isSubmitting }) => (
          <>
            <Field label="First name" name="firstName" component={InputField} />
            <Field label="Last name" name="lastName" component={InputField} />
            <Field label="Street address" name="line1" component={InputField} />
            <Field
              label="Apartment, suite, etc."
              name="line2"
              component={InputField}
            />
            <Field label="City" name="city" component={InputField} />
            <div className={styles.twoColumnContainer}>
              <Field
                label="State"
                name="stateCode"
                component={StateSelectField}
              />
              <Field
                label="Zip code"
                name="postalCode"
                component={InputField}
              />
            </div>
            <div className={styles.phoneNumber}>
              <Field
                label="Phone number"
                name="phone"
                type="tel"
                component={InputField}
                autoComplete="tel-national"
                mask={PHONE_MASK}
                placeholder="(___) ___-____"
              />
            </div>
            <div className={styles.shippingAddressFormButton}>
              <Button fullWidth type="submit" isLoading={isSubmitting}>
                Submit
              </Button>
            </div>
          </>
        )}
      </Form>
    </Modal>
  );
};

function validate(formValues) {
  return {
    firstName: valid(required())(formValues.firstName),
    lastName: valid(required())(formValues.lastName),
    line1: valid(required())(formValues.line1),
    city: valid(required())(formValues.city),
    stateCode: valid(required())(formValues.stateCode),
    postalCode: valid(required(), minLength(5))(formValues.postalCode),
    phone: valid(phone())(formValues.phone),
  };
}

export default CreateShippingAddressModal;
